import { useState } from 'owa-react';
/* eslint-disable-next-line @fluentui/react-components/prefer-fluentui-v9 -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE! Substitute this line with an explanation or resolve the problem by using v9 components instead
 *	> Avoid using FocusZone from '@fluentui/react', as this package has already migrated to Fluent UI 9. Use the equivalent [Tabster](https://tabster.io/) hook instead. */
import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { Button, Tooltip } from '@fluentui/react-components';
import { ChevronLeft12Filled, ChevronRight12Filled, PinRegular } from '@fluentui/react-icons';
import classnames from 'owa-classnames';
import { observer } from 'owa-mobx-react';
import { isMonarchMultipleAccountsEnabled } from 'owa-account-source-list/lib/flights';
import Droppable from 'owa-dnd/lib/components/Droppable';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { isCopilotFeatureEnabled } from 'owa-copilot-settings-utils';
import { getDensityMode, getDensityModeCssClass } from 'owa-fabric-theme';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import isWin32StylingEnabled from 'owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled';
import { type StateChangeSource } from 'owa-left-pane-layout/lib/types/StateChangeSource';
import loc, { isCurrentCultureRightToLeft } from 'owa-localize';
import { navigationPaneScreenReaderOnlyText as Strings_navigationPaneScreenReaderOnlyText } from 'owa-locstrings/lib/strings/navigationpanescreenreaderonlytext.locstring.json';
import {
    hideNavigationPaneButton as Strings_hideNavigationPaneButton,
    pinNavigationPaneButton as Strings_pinNavigationPaneButton,
} from './FolderPane.locstring.json';
import { AccountsTreeView } from 'owa-mail-accounts-view';
import { Favorites } from 'owa-mail-favorites-view';
import setIsDraggedOverState from 'owa-mail-folder-store/lib/actions/setIsDraggedOverState';
import {
    getFavoritesPaneHeight,
    setShowFolderPaneAsTransientOverlay,
    setFavoritesPaneHeightValueMutator,
    shouldShowFolderPaneAsOverlay,
    shouldShowFolderPaneAsTransientOverlay,
    toggleFolderPaneExpansion,
} from 'owa-mail-layout';
import {
    LazyMyOutlookSection,
    lazyDismissMyOutlookFullPage,
    isMyOutlookFullPageActive,
} from './LazyMyOutlookSection';
import { isMyOutlookSectionHidden } from 'owa-personal-os-store/lib/selectors/getReadingPaneState';
import {
    MailFolderTreesParentContainer,
    LazyMailFolderDialogsWrapper,
} from 'owa-mail-folder-tree-container-view';
import React from 'react';
import {
    container,
    containerWillChangeScrollPosition,
    medium,
    compact,
    isChromium as styles_isChromium,
    droppableContainer,
    leftNavSectionWithSectionHeaders,
    containerPadding,
    containerPadding_Win32Style,
    safariScrollHandling,
    favoritesContainer,
    foldersContainer,
    myOutlookPinnedContainer,
    dropContainer,
    contentsContainer,
    scrollContainer,
    touchMode,
    favoritesResizableContainer,
    favoritesResizeHandle,
    transientFolderPaneRoot,
    transientFolderPaneDroppable,
    transientContentsContainer,
    transientFavoritesContainer,
    transientFoldersContainer,
    collapsePaneButtonContainer,
    transientCollapsePaneButtonContainer,
    collapsePaneButton,
    collapsePaneChevron,
} from './FolderPane.scss';
import { getBrowserInfo, type Browser } from 'owa-user-agent';
import { BootState, getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { type FavoritesBitFlagsMasks, getIsBitEnabled, favoritesStore } from 'owa-favorites';
import { getUserConfiguration } from 'owa-session-store';
import { FolderPaneNavigationManager, getMessage } from 'owa-folders-nav';
import { Announced } from 'owa-controls-announced';
import {
    getIsBitSet,
    type ListViewBitFlagsMasks,
} from 'owa-bit-flags/lib/utils/listViewBitFlagsUtil';
import { ResizeHandle, ResizeHandleScenario, ResizeHandleDirection } from 'owa-resize-handle';

enum densitySpacing {
    Compact = '12px',
    Full = '12px',
    Simple = '8px',
}

const pinPaneIcon = <PinRegular />;

const defaultExport: React.FunctionComponent = observer(function FolderPane() {
    const { browser } = getBrowserInfo();
    const isChromium = browser === 'Chrome' || browser === 'Edge Anaheim';
    const isSafari = browser === 'Safari';

    const containerClassNames = classnames(
        container,
        containerWillChangeScrollPosition,
        getDensityModeCssClass(undefined, medium, compact),
        isChromium && styles_isChromium, // show persistent scrollbar in Firefox since it does not support overlay scrollbar
        'customScrollBar',
        'disableTextSelection'
    );

    /* TODO: OW:16444: [Accessibility] [Folder] We should be able to tab into the regular Folder tree even if the Favorites tree exists */
    return (
        <>
            <FocusZone
                key="folderPane"
                className={containerClassNames}
                direction={FocusZoneDirection.vertical}
            >
                <FolderForest isChromium={isChromium} isSafari={isSafari} />
            </FocusZone>
            <LazyMailFolderDialogsWrapper />
        </>
    );
}, 'FolderPane');
export default defaultExport;

export interface FolderForestProps {
    isChromium: boolean;
    isSafari: boolean;
}
const FolderForest = observer(function FolderForest({ isChromium, isSafari }: FolderForestProps) {
    const globalMailboxInfo = getGlobalSettingsAccountMailboxInfo();
    const isWin32StyleEnabled = isWin32StylingEnabled();
    const showAccountsList =
        isMonarchMultipleAccountsEnabled() || isGlobalFeatureEnabled('acct-multiacctcomingsoon');

    const shouldHideFavoritesList = getIsBitSet(16384, globalMailboxInfo);
    const hasFavoritesToShow =
        favoritesStore().outlookFavorites?.size > 0 && !shouldHideFavoritesList;
    const favoritesNodeCount = hasFavoritesToShow ? 1 : 0;
    const shouldPinFavorites = hasFavoritesToShow && getIsBitEnabled(64);
    const stableDropViewState = React.useRef(createDropViewState()).current;
    const emptyDropViewState = isGlobalFeatureEnabled('fwk-stabilize-folder-maillist')
        ? stableDropViewState
        : createDropViewState();
    // Must pass bypassActOnDrop as true so that children nodes' canDrop evaluation
    // is not masked by this component's canDrop, which is always true by default
    const bypassActOnDrop = true;
    // The pre-render work for the global settings account happens in startup
    const globalSettingsBootState = BootState.StartupComplete;
    const shouldShowExpanded = !getUserConfiguration()?.UserOptions?.IsMailRootFolderTreeCollapsed;
    const density = getDensityMode();
    const accountPaddingStyle = React.useMemo(() => {
        return {
            paddingBottom: densitySpacing[density],
            paddingTop: 0,
        } as React.CSSProperties;
    }, [density]);
    const folderPaneRef = React.useRef<HTMLDivElement>(null);
    const [isTouchMode, setTouchMode] = useState(false);
    const showFavoritesResizeHandle =
        isGlobalFeatureEnabled('fp-favorites-resize') && shouldPinFavorites;
    const favoritesHeight = getFavoritesPaneHeight();
    const favoritesContainerRef = React.useRef<HTMLDivElement>(null);

    React.useLayoutEffect(() => {
        if (showFavoritesResizeHandle && favoritesHeight != null) {
            favoritesContainerRef.current?.style.setProperty(
                '--favoritesMaxHeight',
                `${favoritesHeight}px`
            );
        }
    }, [showFavoritesResizeHandle, favoritesHeight]);

    const onFavoritesPaneResized = React.useCallback((newFavoritesHeight: number) => {
        setFavoritesPaneHeightValueMutator(newFavoritesHeight);
    }, []);

    React.useEffect(() => {
        FolderPaneNavigationManager.init(folderPaneRef);
    }, []);

    const onKeyDown = React.useCallback((event: React.KeyboardEvent) => {
        FolderPaneNavigationManager.handleInput(event);
    }, []);

    const onTouchStart = React.useCallback(() => {
        setTouchMode(true);
    }, []);
    const onTouchEnd = React.useCallback(() => {
        setTouchMode(false);
    }, []);
    const onTouchCancel = React.useCallback(() => {
        setTouchMode(false);
    }, []);

    const isPersonalOsEnabled = isCopilotFeatureEnabled('PersonalOS', globalMailboxInfo);

    // Dismiss PersonalOS when user clicks a folder/favorite.
    // Only fires the lazy action when PersonalOS is active to avoid
    // eagerly loading the MyOutlookSection chunk on every folder click.
    const onFolderAreaClick = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
        const target = event.target;
        const isFolderNodeClick =
            target instanceof Element && !!target.closest('[role="treeitem"]');
        if (isMyOutlookFullPageActive() && isFolderNodeClick) {
            lazyDismissMyOutlookFullPage.importAndExecute();
        }
    }, []);

    const scrollContainerClassNames = classnames(
        scrollContainer,
        'customScrollBar',
        isChromium && styles_isChromium,
        isSafari && safariScrollHandling,
        isTouchMode && touchMode
    );
    const folderSectionsScrollContainerClassNames = classnames(
        scrollContainer,
        isWin32StyleEnabled ? 'nativeScrollBar' : 'customScrollBar',
        isChromium && styles_isChromium,
        isSafari && safariScrollHandling,
        isTouchMode && touchMode
    );

    const showWin32OverlayChevron = isWin32StyleEnabled;
    const getDragOverflowContainer = React.useCallback(() => {
        return document.getElementById(
            showWin32OverlayChevron || shouldPinFavorites
                ? 'folderPanePinFavoritesFolderContainer'
                : 'folderPaneDroppableContainer'
        );
    }, [showWin32OverlayChevron, shouldPinFavorites]);

    // Update the drag handlers to track drag state
    const onDragEnter = React.useCallback(() => {
        const draggableContainer = getDragOverflowContainer();
        if (draggableContainer) {
            draggableContainer.style.overflow = 'overlay';
        }
    }, [getDragOverflowContainer]);

    const onDragOver = React.useCallback(() => {
        setIsDraggedOverState(true);
    }, []);

    const onDragLeave = React.useCallback(() => {
        setIsDraggedOverState(false);
        const draggableContainer = getDragOverflowContainer();
        if (draggableContainer) {
            draggableContainer.style.overflow = '';
        }
    }, [getDragOverflowContainer]);

    const onDrop = React.useCallback(() => {
        // NOOP for actual drop handling
    }, []);

    const isWin32TransientOverlay =
        showWin32OverlayChevron && shouldShowFolderPaneAsTransientOverlay();
    const isNarrowOverlay = shouldShowFolderPaneAsOverlay();
    const canPinFolderPane = isWin32TransientOverlay && !isNarrowOverlay;
    const isWin32NonTransient = showWin32OverlayChevron && !isWin32TransientOverlay;

    const onCollapsePane = React.useCallback(() => {
        if (isWin32TransientOverlay && isNarrowOverlay) {
            setShowFolderPaneAsTransientOverlay(false);
        } else {
            toggleFolderPaneExpansion(4);
        }
    }, [isNarrowOverlay, isWin32TransientOverlay]);

    const onPinPane = React.useCallback(() => {
        setShowFolderPaneAsTransientOverlay(false);
        toggleFolderPaneExpansion(
            4,
            true /* persistOnServer */,
            true /* newShowFolderPane */,
            undefined /* newShowFolderPaneOverlayAsBlocking */,
            true /* forcePersistOnServer */
        );
    }, []);

    const collapsePaneIcon = isCurrentCultureRightToLeft() ? (
        <ChevronRight12Filled className={collapsePaneChevron} />
    ) : (
        <ChevronLeft12Filled className={collapsePaneChevron} />
    );
    const collapsePaneLabel = loc(Strings_hideNavigationPaneButton);
    const pinPaneLabel = loc(Strings_pinNavigationPaneButton);

    const folderPaneActionButton = showWin32OverlayChevron ? (
        <div
            className={classnames(
                collapsePaneButtonContainer,
                isWin32TransientOverlay && transientCollapsePaneButtonContainer
            )}
        >
            <Tooltip
                relationship="label"
                positioning="below"
                withArrow={true}
                content={canPinFolderPane ? pinPaneLabel : collapsePaneLabel}
            >
                <Button
                    aria-label={canPinFolderPane ? pinPaneLabel : collapsePaneLabel}
                    appearance="subtle"
                    icon={canPinFolderPane ? pinPaneIcon : collapsePaneIcon}
                    onClick={canPinFolderPane ? onPinPane : onCollapsePane}
                    className={collapsePaneButton}
                />
            </Tooltip>
        </div>
    ) : null;

    const useSplitScrollContainers = showWin32OverlayChevron;
    const usePinnedFavoritesContainers = useSplitScrollContainers || shouldPinFavorites;

    const folderPaneTreeClassName = classnames(
        usePinnedFavoritesContainers ? contentsContainer : containerPadding,
        useSplitScrollContainers && transientContentsContainer,
        useSplitScrollContainers && !shouldPinFavorites && containerPadding,
        isWin32NonTransient && containerPadding_Win32Style
    );

    const favoritesContainerClassName =
        hasFavoritesToShow && usePinnedFavoritesContainers
            ? classnames(
                  favoritesContainer,
                  useSplitScrollContainers && transientFavoritesContainer,
                  folderSectionsScrollContainerClassNames,
                  showFavoritesResizeHandle && favoritesResizableContainer
              )
            : undefined;

    const foldersContainerClassName = usePinnedFavoritesContainers
        ? classnames(
              foldersContainer,
              useSplitScrollContainers && transientFoldersContainer,
              folderSectionsScrollContainerClassNames
          )
        : undefined;

    const baseDroppableClassName = shouldPinFavorites
        ? dropContainer
        : classnames(droppableContainer, folderSectionsScrollContainerClassNames);

    const transientDroppableClassName = classnames(
        transientFolderPaneDroppable,
        shouldPinFavorites ? dropContainer : droppableContainer
    );

    const droppableProps = {
        id: 'folderPaneDroppableContainer',
        onDragOver,
        onDrop,
        onDragEnter,
        onDragLeave,
        dropViewState: emptyDropViewState,
        greedy: true,
        bypassActOnDrop,
    };

    const folderPaneTree = (
        <div
            className={folderPaneTreeClassName}
            ref={folderPaneRef}
            onKeyDown={onKeyDown}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchCancel}
            role="tree"
        >
            <span className="screenReaderOnly" role="heading" aria-level={2}>
                {loc(Strings_navigationPaneScreenReaderOnlyText)}
            </span>
            {isPersonalOsEnabled && !isMyOutlookSectionHidden() && (
                <LazyMyOutlookSection
                    className={
                        shouldPinFavorites
                            ? classnames(myOutlookPinnedContainer, scrollContainerClassNames)
                            : leftNavSectionWithSectionHeaders
                    }
                    style={shouldPinFavorites ? undefined : accountPaddingStyle}
                    mailboxInfo={globalMailboxInfo}
                />
            )}
            <div
                ref={favoritesContainerRef}
                className={favoritesContainerClassName}
                onClickCapture={isPersonalOsEnabled ? onFolderAreaClick : undefined}
            >
                {hasFavoritesToShow && (
                    <Favorites
                        key="favorites"
                        className={
                            shouldPinFavorites
                                ? undefined
                                : classnames(leftNavSectionWithSectionHeaders)
                        }
                        style={shouldPinFavorites ? undefined : accountPaddingStyle}
                    />
                )}
            </div>
            {showFavoritesResizeHandle && (
                <ResizeHandle
                    direction={ResizeHandleDirection.horizontal}
                    scenario={ResizeHandleScenario.FavoritesPane}
                    className={favoritesResizeHandle}
                    onResized={onFavoritesPaneResized}
                />
            )}
            <div
                id="folderPanePinFavoritesFolderContainer"
                className={foldersContainerClassName}
                onClickCapture={isPersonalOsEnabled ? onFolderAreaClick : undefined}
            >
                {!showAccountsList && (
                    <MailFolderTreesParentContainer
                        accountMailboxInfo={globalMailboxInfo}
                        accountBootState={globalSettingsBootState}
                        favoritesNodeCount={favoritesNodeCount}
                        isExpanded={shouldShowExpanded}
                        ellipsesOnHover={true}
                    />
                )}
                {showAccountsList && (
                    <AccountsTreeView
                        key="accounts"
                        favoritesNodeCount={favoritesNodeCount}
                        ellipsesOnHover={true}
                    />
                )}
            </div>
        </div>
    );

    return showWin32OverlayChevron ? (
        <div className={transientFolderPaneRoot}>
            {folderPaneActionButton}
            <Droppable {...droppableProps} classNames={transientDroppableClassName}>
                {folderPaneTree}
                <Announced message={getMessage()} />
            </Droppable>
        </div>
    ) : (
        <Droppable {...droppableProps} classNames={baseDroppableClassName}>
            {folderPaneActionButton}
            {folderPaneTree}
            <Announced message={getMessage()} />
        </Droppable>
    );
}, 'FolderForest');
